/**
 * @file AddBlockMenu.tsx
 * @description [+ Добавить блок] в структуре страницы конструктора
 * (phase19.16 Р1-6а; Р2 — uiux-design-r2 §5, решение пользователя
 * 2026-10-09): прежний список 14 типов убран — кнопка открывает библиотеку
 * заготовок (`PresetLibraryDialog`). Место вставки — после выбранного блока
 * (иначе в конец) или заданное (`after`, «+ блок сюда» между блоками).
 * На 30 блоках кнопка выключена с объяснением (SB64).
 */

import { Plus } from "lucide-react";

import { Button } from "../../ui/button";
import { useBuilder } from "../builder-context";
import { BLOCKS_FULL_TEXT } from "../../../lib/site-builder/block-limits";
import { cn } from "@/lib/utils";

interface Props {
  disabled?: boolean;
  /** Куда вставлять: id блока выше, `null` — в начало; нет — после выбранного. */
  after?: string | null;
  label?: string;
  /** Тонкая строка «+ блок сюда» между блоками. */
  inline?: boolean;
  /** Почему кнопка выключена (у шаблона товара — свой лимит). */
  fullText?: string;
}

export function AddBlockMenu({ disabled = false, after, label = "Добавить блок", inline = false, fullText = BLOCKS_FULL_TEXT }: Props) {
  const b = useBuilder();
  if (inline) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => b.openLibrary(after)}
        className={cn(
          // Видимая полоса 24, зона нажатия 48 (padding + отрицательные поля): соседние строки не раздвигаются.
          "group relative z-10 -my-3 flex h-12 w-full items-center gap-2 rounded-md px-3 py-3 text-on-surface-variant outline-none",
          "hover:text-primary focus-visible:ring-2 focus-visible:ring-ring disabled:hidden",
          // На ПК — по наведению и фокусу, чтобы 30 строк не рябили; на касании видно всегда.
          // ПК (мышь): полоса 24, наезжает на поля строк (по 6 px) — шаг списка ≤ 90 px; на текст строк не заходит.
          "xl:-my-1.5 xl:h-6 xl:py-0 xl:opacity-0 xl:hover:opacity-100 xl:focus-visible:opacity-100",
        )}
      >
        <span aria-hidden className="h-px flex-1 bg-outline-variant group-hover:bg-primary/40" />
        <span className="type-caption inline-flex items-center gap-1">
          <Plus aria-hidden className="size-3.5" />
          блок сюда
        </span>
        <span aria-hidden className="h-px flex-1 bg-outline-variant group-hover:bg-primary/40" />
      </button>
    );
  }
  return (
    <Button
      variant="outline"
      className="w-full justify-start"
      disabled={disabled}
      title={disabled ? fullText : undefined}
      onClick={() => b.openLibrary(after)}
    >
      <Plus aria-hidden />
      {label}
    </Button>
  );
}
